import React from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { Box, Typography } from '@mui/material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';

import CustomDialog from 'src/components/UI/dialog/CustomDialog';
import TextInput from 'src/components/UI/input/TextInput';
import { useAutoTranslation } from 'src/hooks/useTranslate';

const validationSchema = yup.object({
  FormTitle: yup
    .string('Enter the form title')
    .required('This field must be specified'),
  FormDescription: yup
    .string('Enter the form description')
    .required('This field must be specified')
});

export default function NewFormModal({ open, onClose, onCreate }) {
  const t = useAutoTranslation();

  const formik = useFormik({
    initialValues: { FormTitle: '', FormDescription: '' },
    validationSchema,
    onSubmit: async (values) => {
      await onCreate(values);
    }
  });

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  return (
    <CustomDialog
      open={open}
      title="Create New Form"
      handleClose={handleClose}
      showActions
      saveBtnLabel="Create form"
      cancelBtnLabel="Cancel"
      onSave={() => formik.submitForm()}
      maxWidth="sm"
    >
      <form onSubmit={formik.handleSubmit}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.5 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 44,
              height: 44,
              borderRadius: '50%',
              color: 'var(--primary-color, #2ba9bf)',
              background: 'linear-gradient(135deg, #e6f6fa 0%, #eef0fb 100%)',
              flexShrink: 0
            }}
          >
            <DescriptionOutlinedIcon />
          </Box>
          <Typography
            variant="body2"
            sx={{ color: '#6b7a8d', lineHeight: 1.5 }}
          >
            {t(
              'Give your form a name and a short description. You can build out sections and questions in the next step.'
            )}
          </Typography>
        </Box>

        <Box sx={{ mt: 1 }}>
          <TextInput
            id="FormTitle"
            name="FormTitle"
            label={t('Form Title')}
            placeholder={t('e.g. Family History Questionnaire')}
            required
            value={formik.values.FormTitle}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.FormTitle && Boolean(formik.errors.FormTitle)}
            helperText={formik.touched.FormTitle && formik.errors.FormTitle}
          />
        </Box>

        <Box sx={{ mt: 2 }}>
          <TextInput
            type="textarea"
            id="FormDescription"
            name="FormDescription"
            label={t('Form Description')}
            placeholder={t('Briefly describe what this form is for')}
            required
            rows={3}
            value={formik.values.FormDescription}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.FormDescription &&
              Boolean(formik.errors.FormDescription)
            }
            helperText={
              formik.touched.FormDescription && formik.errors.FormDescription
            }
          />
        </Box>
        {/* hidden submit keeps Enter-to-submit working inside the dialog */}
        <button type="submit" style={{ display: 'none' }} aria-hidden />
      </form>
    </CustomDialog>
  );
}
